import { useEffect, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Fade,
  makeStyles,
  Modal,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Carousel from "react-material-ui-carousel";
import componentStylesCardDeck from "assets/theme/components/cards/card-deck";
import componentStyles from "assets/theme/views/admin/dashboard";
import { IsMobile } from "utils/is-mobile";
import {
  handleCatalogImageLoadError,
  isDefaultCatalogProductImage,
} from "./catalog-produto-image-default";

const ACCEPTED_FILE_TYPE = "image/jpeg";

const useStyles = makeStyles(componentStyles);
const useMyStyles = makeStyles(() => ({
  th: {
    color: "#32325d",
    fontSize: ".75rem",
    fontFamily: "inherit",
    fontWeight: 600,
    lineHeight: 1.5,
    marginBottom: ".5rem",
    textAlign: "center",
    paddingBottom: "15px",
  },
  td: {
    fontSize: ".75rem",
    fontFamily: "inherit",
    fontWeight: 500,
    textAlign: "center",
    padding: "10px 0px",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
}));
const useStylesCardDeck = makeStyles(componentStylesCardDeck);

export default function ModalShowImage({
  showingPhoto,
  setShowingPhoto,
  loading,
  setLoading,
  handleDeleteImage,
  handleFileChange,
}) {
  const classes = {
    ...useStyles(),
    ...useStylesCardDeck(),
    ...useMyStyles(),
  };
  const isMobile = IsMobile();
  const verifyProfiles = JSON.parse(localStorage.getItem("userProfiles"));
  const verifyProfilesVision = JSON.parse(
    localStorage.getItem("userProfilesVision"),
  );

  const userProfiles = verifyProfilesVision
    ? verifyProfilesVision
    : verifyProfiles;

  const isAgent = userProfiles.find((profile) =>
    ["ADM", "CRC", "SUPORTE", "KEY USER"].includes(profile.NomePerfil),
  );

  const fileInputRef = useRef(null);

  const galleryList = showingPhoto?.galleryPhotos?.length
    ? showingPhoto.galleryPhotos
    : showingPhoto
      ? [showingPhoto]
      : [];

  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    if (!showingPhoto) {
      setSlideIndex(0);
      return;
    }
    const list = showingPhoto.galleryPhotos?.length
      ? showingPhoto.galleryPhotos
      : [showingPhoto];
    const idx = list.findIndex((p) => p.id === showingPhoto.id);
    setSlideIndex(idx >= 0 ? idx : 0);
  }, [showingPhoto]);

  const handleCarouselChange = (now) => {
    if (typeof now !== "number") return;
    setSlideIndex(now);
    setShowingPhoto((prev) => {
      const list = prev?.galleryPhotos?.length
        ? prev.galleryPhotos
        : prev
          ? [prev]
          : [];
      const next = list[now];
      if (!next) return prev;
      return {
        ...next,
        galleryPhotos: list,
      };
    });
  };

  const handleUploadPhoto = () => {
    fileInputRef.current?.click();
  };

  const activePhoto = galleryList[slideIndex] || showingPhoto;

  return (
    <>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className={classes.modal}
        open={Boolean(showingPhoto)}
        onClose={() => setShowingPhoto(null)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        style={{
          display: "flex",
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
        }}
      >
        <Fade in={Boolean(showingPhoto)}>
          <div
            className={classes.paper}
            style={{
              backgroundColor: "#FFF",
              padding: "20px",
              borderRadius: 8,
            }}
          >
            <Box
              display="flex"
              justifyContent={isMobile ? "space-around" : "flex-end"}
              marginBottom="1rem"
            >
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: isMobile ? 0 : "1rem",
                  cursor: "pointer",
                }}
                disabled={loading.delete}
                onClick={() => setShowingPhoto(null)}
              >
                <Typography variant="caption">Fechar</Typography>
                <CloseIcon
                  onClick={() => setShowingPhoto(null)}
                  disabled={loading.delete}
                  style={{ marginLeft: "0.5rem" }}
                />
              </Box>
            </Box>
            <Box>
              {galleryList.length > 1 ? (
                <Box style={{ maxWidth: 520, margin: "0 auto" }}>
                  <Carousel
                    index={slideIndex}
                    onChange={handleCarouselChange}
                    autoPlay={false}
                    indicators
                    swipe
                    cycleNavigation
                    navButtonsAlwaysVisible
                    NextIcon={<NavigateNextIcon />}
                    PrevIcon={<NavigateBeforeIcon />}
                    navButtonsWrapperProps={{
                      style: { bottom: 8, top: "unset" },
                    }}
                  >
                    {galleryList.map((photo) => (
                      <Box
                        key={`${photo.productId}-${photo.id}`}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        style={{ minHeight: 320 }}
                      >
                        <img
                          alt=""
                          src={photo.url}
                          style={{
                            maxWidth: "100%",
                            width: 500,
                            height: 420,
                            objectFit: "contain",
                          }}
                          onError={handleCatalogImageLoadError}
                        />
                      </Box>
                    ))}
                  </Carousel>
                </Box>
              ) : (
                <Box display="flex" justifyContent="center">
                  <img
                    alt=""
                    src={activePhoto?.url}
                    width={500}
                    height={420}
                    onError={handleCatalogImageLoadError}
                  />
                </Box>
              )}
            </Box>
            <Box display="flex" justifyContent="center" marginTop="1rem">
              {isDefaultCatalogProductImage(activePhoto?.url) ? (
                <Button
                  variant="contained"
                  color="default"
                  onClick={handleUploadPhoto}
                  disabled={loading.upload}
                >
                  {loading.upload ? "Enviando..." : "Enviar nova imagem"}
                  <CloudUploadIcon
                    style={{ width: 20, height: 20, marginLeft: 8 }}
                  />
                </Button>
              ) : loading.deleteConfirm ? (
                <Box>
                  <Typography as="p" color="initial">
                    Tem certeza que deseja deletar a foto?
                  </Typography>
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="center"
                    marginTop="0.5rem"
                  >
                    <Button
                      variant="contained"
                      color="secondary"
                      style={{ marginRight: 12 }}
                      onClick={() =>
                        setLoading((prev) => ({
                          ...prev,
                          deleteConfirm: false,
                        }))
                      }
                      disabled={loading.delete}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      color="default"
                      disabled={loading.delete}
                      onClick={handleDeleteImage}
                    >
                      {loading.delete ? "Removendo..." : "Remover"}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  {isAgent && (
                    <Button
                      variant="contained"
                      color="default"
                      onClick={() =>
                        setLoading((prev) => ({ ...prev, deleteConfirm: true }))
                      }
                    >
                      Remover
                      <DeleteIcon
                        style={{ width: 20, height: 20, marginLeft: 8 }}
                      />
                    </Button>
                  )}
                </>
              )}
            </Box>
          </div>
        </Fade>
      </Modal>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept={ACCEPTED_FILE_TYPE}
        style={{ display: "none" }}
      />
    </>
  );
}
